import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

const HERO_IMG = "https://images.unsplash.com/photo-1593696140826-c58b021acf8b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBsaXZpbmclMjByb29tJTIwdHZ8ZW58MHx8fHwxNzgwNTY3MjAwfDA&ixlib=rb-4.1.0&q=85";

export default function LoginPage({ mode = "login" }) {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(mode); // login | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate("/dashboard"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "login") await login(email, password);
      else await register(email, password, name);
      toast.success(tab === "login" ? "Welcome back" : "Account created");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.message || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      {/* Left - editorial image */}
      <div className="hidden lg:block relative overflow-hidden" style={{ background: "#1C1C1C" }}>
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(28,28,28,0.2) 0%, rgba(28,28,28,0.65) 100%)" }}/>
        <div className="absolute top-12 left-12 right-12 text-white">
          <div className="text-[11px] tracking-[0.32em] font-bold" style={{ color: "#A68A64", lineHeight: 1.05 }}>SUBTEND</div>
          <div className="text-[11px] tracking-[0.32em] font-bold text-white mt-1" style={{ lineHeight: 1.05 }}>STUDIO</div>
          <h1 className="font-serif font-light mt-6" style={{ fontSize: "44px", lineHeight: 1.05 }}>
            Design. Visualise.<br/>Price. <span style={{ color: "#C8A97E", fontStyle: "italic" }}>Sell.</span>
          </h1>
        </div>
        <div className="absolute bottom-12 left-12 right-12 text-white/80 text-sm max-w-md">
          Design-to-proposal software built for media-wall businesses.
        </div>
      </div>

      {/* Right - form */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md page-anim">
          <Link to="/" className="inline-block" data-testid="brand-link" aria-label="Subtend Studio">
            <div className="text-[11px] tracking-[0.32em] font-bold" style={{ color: "#A68A64", lineHeight: 1.05 }}>SUBTEND</div>
            <div className="text-[11px] tracking-[0.32em] font-bold text-foreground mt-1" style={{ lineHeight: 1.05 }}>STUDIO</div>
          </Link>
          <h2 className="font-serif font-light mt-4" style={{ fontSize: "40px", lineHeight: 1.05 }}>
            {tab === "login" ? "Welcome back." : "Create your studio."}
          </h2>
          <p className="text-sm text-muted-foreground mt-2 mb-8">
            {tab === "login" ? "Sign in to access your projects, clients and proposals." : "Begin designing premium media walls for your clients."}
          </p>

          <div className="flex border-b border-border mb-6">
            <button
              data-testid="tab-login"
              onClick={() => setTab("login")}
              className={`pb-3 px-4 text-sm tracking-wide ${tab === "login" ? "border-b-2 border-foreground font-medium" : "text-muted-foreground"}`}>
              Sign in
            </button>
            <button
              data-testid="tab-register"
              onClick={() => setTab("register")}
              className={`pb-3 px-4 text-sm tracking-wide ${tab === "register" ? "border-b-2 border-foreground font-medium" : "text-muted-foreground"}`}>
              Create account
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {tab === "register" && (
              <div>
                <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>Your name</label>
                <input data-testid="input-name" className="luxe-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" />
              </div>
            )}
            <div>
              <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>Email</label>
              <input data-testid="input-email" type="email" className="luxe-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@studio.com" required />
            </div>
            <div>
              <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>Password</label>
              <input data-testid="input-password" type="password" className="luxe-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required minLength={6} />
            </div>
            <button data-testid={tab === "login" ? "btn-login-submit" : "btn-register-submit"} type="submit" className="btn-primary w-full justify-center mt-2" disabled={loading}>
              {loading ? "Please wait..." : (tab === "login" ? "Sign in" : "Create account")}
            </button>
          </form>

          <div className="flex items-center gap-3 my-6 text-xs text-muted-foreground">
            <div className="flex-1 h-px bg-border" />
            <span className="overline">Or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <button data-testid="btn-google-login" onClick={handleGoogle} className="btn-outline w-full justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </button>

          <p className="text-xs text-muted-foreground mt-8 text-center">
            By continuing you agree to our <a href="/terms" data-testid="link-terms" className="underline">Terms of Service</a> and acknowledge our <a href="/privacy" data-testid="link-privacy" className="underline">Privacy Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
